import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./assets/css/style.css";
import "./assets/css/bootstrap.min.css";
import "../emagazine/assets/css/new_style.css";
import HeaderNavbar from "../../Components/BybMainPage/BybNavbar/HeaderNavbar.jsx";
import BybFooter from "../../Components/BybMainPage/BybFooter/BybFooter.jsx";
import PageSeo from "../../Components/Common/Seo/PageSeo.jsx";
import { DEFAULT_OG_IMAGE, SITE_URL, toCanonicalUrl } from "../../Components/Common/Seo/seoUtils.js";
import {
  buildEmagazineApiUrl,
  buildEmagazineArticlePath,
  buildEmagazineUploadUrl,
} from "./emagazineApi.js";
import {
  AUTHOR_JOB_TITLE,
  buildAuthorPath,
  buildAuthorPersonSchema,
  getArticleAuthorName,
  getAuthorBio,
  getAuthorProfileByName,
  getAuthorProfileBySlug,
} from "./authorData.js";

const AuthorProfilePage = () => {
  const { authorSlug } = useParams();
  const authorProfile = getAuthorProfileBySlug(authorSlug);
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchArticles = async () => {
      setIsLoading(true);

      try {
        const response = await fetch(buildEmagazineApiUrl("get_all_blogs.php"));
        const data = await response.json();
        const blogs = Array.isArray(data.blogs) ? data.blogs : [];
        const filteredArticles = blogs
          .filter((blog) => Number(blog.approved) === 1)
          .filter((blog) => {
            const blogAuthor = getAuthorProfileByName(getArticleAuthorName(blog));
            return blogAuthor?.slug === authorProfile?.slug;
          })
          .map((blog) => ({
            ...blog,
            url: buildEmagazineArticlePath(blog.category, blog.slug),
          }));

        if (isMounted) {
          setArticles(filteredArticles);
        }
      } catch (error) {
        if (isMounted) {
          setArticles([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchArticles();

    return () => {
      isMounted = false;
    };
  }, [authorProfile?.slug]);

  const authorSchema = useMemo(
    () => buildAuthorPersonSchema(authorProfile, articles),
    [authorProfile, articles]
  );
  const profilePageSchema = authorProfile ? {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "@id": `${toCanonicalUrl(buildAuthorPath(authorProfile))}#profilepage`,
    name: `${authorProfile.name} Author Profile`,
    url: toCanonicalUrl(buildAuthorPath(authorProfile)),
    mainEntity: {
      "@id": `${toCanonicalUrl(buildAuthorPath(authorProfile))}#person`,
    },
    isPartOf: {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
    },
  } : null;
  const articleListSchema = articles.length ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${toCanonicalUrl(buildAuthorPath(authorProfile))}#articles`,
    name: `${authorProfile.name} articles on Brew Your Brand`,
    itemListElement: articles.map((article, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: toCanonicalUrl(article.url),
      name: article.title,
    })),
  } : null;

  if (!authorProfile) {
    return null;
  }

  const title = `${authorProfile.name} | Brew Your Brand Author`;
  const description = getAuthorBio(authorProfile);

  return (
    <>
      <PageSeo
        title={title}
        description={description}
        canonical={buildAuthorPath(authorProfile)}
        ogTitle={title}
        ogDescription={description}
        ogImage={DEFAULT_OG_IMAGE}
        structuredData={[authorSchema, profilePageSchema, articleListSchema]}
      />
      <HeaderNavbar legacyNavigation />
      <section className="author-profile-page">
        <div className="container">
          <div className="author-profile-header">
            <p className="author-profile-label">BYB eMagazine Author</p>
            <h1>{authorProfile.name}</h1>
            <p className="author-profile-title">{AUTHOR_JOB_TITLE}</p>
            <p className="author-profile-bio">{description}</p>
          </div>

          <div className="author-profile-articles">
            <h2>Articles by {authorProfile.name}</h2>
            {isLoading ? (
              <p>Loading articles...</p>
            ) : articles.length ? (
              <div className="row g-4">
                {articles.map((article) => (
                  <div className="col-md-6" key={article.id || article.slug}>
                    <article className="author-article-card">
                      {article.image2 && (
                        <a href={article.url}>
                          <img
                            src={buildEmagazineUploadUrl(article.image2)}
                            alt={article.title ? `${article.title} article thumbnail` : "BYB eMagazine article thumbnail"}
                          />
                        </a>
                      )}
                      <div>
                        <p>{article.category}</p>
                        <h3><a href={article.url}>{article.title}</a></h3>
                      </div>
                    </article>
                  </div>
                ))}
              </div>
            ) : (
              <p>No articles are currently listed for this author.</p>
            )}
          </div>
        </div>
      </section>
      <BybFooter />
    </>
  );
};

export default AuthorProfilePage;